import React from 'react';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import { motion } from 'framer-motion';
import { FileText, Mail, Phone } from 'lucide-react';

export default function CGU() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <BrandLink />
          <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Retour à l'accueil
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-emerald-100 p-3 rounded-xl">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Conditions Générales d'Utilisation (CGU)
              </h1>
              <p className="text-slate-500 mt-1">
                Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            {/* 1. PRÉAMBULE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Préambule</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                TrésoPilot est une application web SaaS destinée aux micro-entrepreneurs français, leur permettant d'anticiper leur trésorerie, 
                leurs échéances légales (URSSAF, TVA, CFE, impôts) et les montants à mettre de côté pour leurs charges sociales et fiscales.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») régissent l'accès et l'utilisation de l'application 
                TrésoPilot (ci-après « le Service » ou « l'Application ») ainsi que du site web accessible à l'adresse <strong>HTTPS://TRESOPILOT.FR</strong> 
                (ci-après « le Site »).
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-4">
                <p className="text-amber-900 font-semibold mb-2">⚠️ Avertissement important</p>
                <p className="text-amber-800 leading-relaxed">
                  Le Service propose des estimations, calculs indicatifs et rappels d'échéances. <strong>Il ne constitue en aucun cas 
                  un service de comptabilité, d'expertise-comptable, de conseil fiscal ou juridique.</strong> Les montants, dates et 
                  recommandations fournis sont donnés à titre informatif uniquement. L'Utilisateur reste seul responsable de la 
                  vérification de ses obligations fiscales et sociales auprès des organismes officiels (URSSAF, impots.gouv.fr, etc.).
                </p>
              </div>
            </section>

            {/* 2. IDENTIFICATION DE L'ÉDITEUR */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Identification de l'éditeur (Loi LCEN)</h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-2">
                <p><strong>Éditeur :</strong> TrésoPilot</p>
                <p><strong>Statut :</strong> Micro-entreprise</p>
                <p><strong>Adresse :</strong> 60 rue François Ier, 75008 Paris, France</p>
                <p><strong>Email :</strong> <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a></p>
                <p><strong>Téléphone / WhatsApp Business :</strong> +33 7 56 90 15 80</p>
                <p><strong>SIRET :</strong> 952 476 950 00011</p>
                <p><strong>Directeur de publication :</strong> Équipe Communication TrésoPilot</p>
                <p><strong>Hébergement :</strong> IONOS</p>
              </div>
            </section>

            {/* 3. DÉFINITIONS */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Définitions</h2>
              <ul className="space-y-2 text-slate-700">
                <li><strong>« Utilisateur »</strong> : toute personne physique ou morale accédant au Service, qu'elle soit inscrite ou non.</li>
                <li><strong>« Compte »</strong> : espace personnel sécurisé créé lors de l'inscription permettant d'accéder aux fonctionnalités du Service.</li>
                <li><strong>« Services »</strong> : ensemble des fonctionnalités proposées par TrésoPilot (tableau de bord, calculs, rappels, exports, etc.).</li>
                <li><strong>« Abonnement »</strong> : formule d'accès payante récurrente (mensuelle ou annuelle) donnant accès aux plans Plus ou Pro.</li>
                <li><strong>« Pack 60 jours »</strong> : formule d'accès payante ponctuelle donnant accès aux fonctionnalités du plan Plus pendant 60 jours.</li>
                <li><strong>« Contenu »</strong> : tous textes, images, graphiques, calculs, interfaces présents sur le Site et dans l'Application.</li>
                <li><strong>« Données »</strong> : informations saisies par l'Utilisateur (chiffre d'affaires, charges, paramètres fiscaux, etc.).</li>
                <li><strong>« Espace Client »</strong> : interface accessible après connexion permettant de gérer son compte et ses données.</li>
                <li><strong>« Stripe »</strong> : prestataire de services de paiement en ligne utilisé pour la gestion des transactions.</li>
              </ul>
            </section>

            {/* 4. OBJET & CHAMP D'APPLICATION */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Objet et champ d'application</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Les présentes CGU ont pour objet de définir les conditions dans lesquelles l'Utilisateur peut accéder et utiliser le Service.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                Elles s'appliquent à tous les Utilisateurs, qu'ils soient utilisateurs gratuits (plan Free) ou payants (plans Plus, Pro, ou Pack 60 jours).
              </p>
              <p className="text-slate-700 leading-relaxed">
                <strong>Statut de l'Utilisateur :</strong> Certaines dispositions (notamment le droit de rétractation, la médiation de la consommation) 
                s'appliquent différemment selon que l'Utilisateur agit en tant que consommateur (au sens du Code de la consommation) ou en tant que professionnel.
              </p>
            </section>

            {/* 5. ACCEPTATION */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Acceptation des CGU</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'utilisation du Service implique l'acceptation pleine et entière des présentes CGU. Lors de l'inscription, l'Utilisateur 
                doit cocher une case attestant qu'il a lu et accepté les CGU.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Capacité juridique :</strong> L'Utilisateur déclare avoir la capacité juridique de contracter (majeur non protégé, 
                ou personne morale dûment représentée). L'utilisation du Service à des fins professionnelles implique que l'Utilisateur 
                agit dans le cadre de son activité.
              </p>
            </section>

            {/* 6. ACCÈS AU SERVICE / CRÉATION DE COMPTE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Accès au Service / Création de compte</h2>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li>L'Utilisateur crée un compte en fournissant des informations exactes et à jour (nom, email, etc.).</li>
                <li>Un seul compte par personne/entité est autorisé.</li>
                <li>L'Utilisateur est responsable de la confidentialité de ses identifiants de connexion.</li>
                <li>Toute utilisation du compte est présumée effectuée par l'Utilisateur titulaire.</li>
                <li>En cas d'utilisation frauduleuse, abusive, ou de menace pour la sécurité du Service, l'éditeur se réserve le droit 
                de suspendre ou supprimer le compte sans préavis ni indemnité.</li>
              </ul>
            </section>

            {/* 7. DESCRIPTION DES SERVICES */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Description des services</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                TrésoPilot propose plusieurs formules d'accès :
              </p>
              
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-bold text-slate-900 mb-2">Plan Free (Gratuit)</h3>
                  <ul className="list-disc pl-6 text-slate-700 space-y-1">
                    <li>Tableau de bord de base</li>
                    <li>1 activité</li>
                    <li>Historique limité à 3 mois</li>
                    <li>Calculs URSSAF de base</li>
                    <li>Prochaines échéances</li>
                    <li>Pas d'enveloppes virtuelles</li>
                    <li>Pas d'exports PDF/CSV</li>
                    <li>Rappels limités (in-app uniquement)</li>
                  </ul>
                </div>

                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <h3 className="font-bold text-slate-900 mb-2">Plan Plus (Payant - Abonnement)</h3>
                  <ul className="list-disc pl-6 text-slate-700 space-y-1">
                    <li>Historique illimité</li>
                    <li>Enveloppes virtuelles</li>
                    <li>Rappels avancés (email + notifications in-app)</li>
                    <li>Exports PDF & CSV</li>
                    <li>Support prioritaire</li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <h3 className="font-bold text-slate-900 mb-2">Plan Pro (Payant - Abonnement)</h3>
                  <ul className="list-disc pl-6 text-slate-700 space-y-1">
                    <li>Toutes les fonctionnalités Plus</li>
                    <li>Multi-activités</li>
                    <li>Alertes seuils TVA et plafonds</li>
                    <li>Simulations avancées</li>
                    <li>Lien de partage comptable (FONCTIONNALITÉ À VENIR)</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                  <h3 className="font-bold text-slate-900 mb-2">Pack 60 jours (Paiement unique)</h3>
                  <ul className="list-disc pl-6 text-slate-700 space-y-1">
                    <li>Accès complet aux fonctionnalités du plan Plus pendant 60 jours</li>
                    <li>Pas d'abonnement récurrent</li>
                    <li>Expire automatiquement à la date indiquée</li>
                    <li>Idéal pour tester avant de s'engager</li>
                  </ul>
                </div>
              </div>

              <p className="text-slate-600 text-sm mt-4 italic">
                Les fonctionnalités et caractéristiques de chaque plan sont susceptibles d'évoluer. L'éditeur s'efforce d'informer 
                les Utilisateurs de tout changement significatif.
              </p>
            </section>

            {/* 8. PRIX — ABONNEMENTS — FACTURATION */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Prix — Abonnements — Facturation</h2>
              
              <h3 className="font-bold text-slate-800 mb-2 mt-4">8.1 Tarifs</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Les tarifs en vigueur sont affichés sur la page Abonnement du Site et de l'Application. Tous les prix sont indiqués 
                en euros (EUR) toutes taxes comprises (TTC).
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">8.2 Abonnements récurrents (Plus / Pro)</h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Les abonnements sont disponibles en formule <strong>mensuelle</strong> ou <strong>annuelle</strong>.</li>
                <li>Le paiement est effectué par avance pour la période souscrite.</li>
                <li>L'abonnement se renouvelle automatiquement à la fin de chaque période, sauf résiliation avant la date de renouvellement.</li>
                <li>En cas de changement de plan (upgrade vers Plus ou Pro), le nouveau tarif s'applique immédiatement. 
                En cas de downgrade, le changement prend effet à la fin de la période de facturation en cours.</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">8.3 Pack 60 jours</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Le Pack 60 jours fait l'objet d'un paiement unique. Il n'est pas récurrent et expire automatiquement à l'issue des 60 jours 
                suivant son activation.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">8.4 Paiement et facturation</h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Les paiements sont traités par <strong>Stripe</strong>, prestataire de services de paiement sécurisé.</li>
                <li>Les factures et reçus sont disponibles par email et dans le portail client Stripe accessible via le bouton 
                « Gérer mon abonnement » de l'Application.</li>
                <li>En cas d'échec de paiement (payment_failed), l'accès au Service peut être restreint ou suspendu jusqu'à régularisation.</li>
                <li>L'Utilisateur s'engage à maintenir des informations de paiement valides et à jour.</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">8.5 Évolution des tarifs</h3>
              <p className="text-slate-700 leading-relaxed">
                L'éditeur se réserve le droit de modifier ses tarifs. Les nouveaux tarifs s'appliquent aux nouveaux abonnements. 
                Pour les abonnements en cours, l'Utilisateur est informé par email au moins 30 jours avant l'application 
                des nouveaux tarifs. L'Utilisateur peut alors choisir de résilier son abonnement.
              </p>
            </section>

            {/* 9. DROIT DE RÉTRACTATION */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Droit de rétractation (Consommateurs uniquement)</h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="text-blue-900 font-semibold mb-2">ℹ️ Information</p>
                <p className="text-blue-800">
                  Les dispositions suivantes s'appliquent uniquement aux Utilisateurs agissant en qualité de <strong>consommateurs</strong> 
                  au sens du Code de la consommation français. Les professionnels ne bénéficient pas d'un droit de rétractation légal.
                </p>
              </div>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">9.1 Principe</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Conformément aux articles L. 221-18 et suivants du Code de la consommation, le consommateur dispose d'un délai de 
                <strong> 14 jours</strong> pour exercer son droit de rétractation sans avoir à justifier de motifs ni à payer de pénalité.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">9.2 Exception pour les contenus numériques</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'article L. 221-28 du Code de la consommation prévoit que le droit de rétractation ne peut être exercé pour les contenus 
                numériques fournis immédiatement, si l'exécution a commencé avec l'accord préalable exprès du consommateur et renoncement 
                exprès à son droit de rétractation.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                Lors de la souscription, une case à cocher est proposée avec la mention suivante (ou similaire) :
              </p>
              <div className="bg-slate-100 rounded-lg p-4 italic text-slate-700 mb-3">
                <p className="mb-2">
                  ☐ « Je demande l'exécution immédiate du service numérique TrésoPilot et reconnais être informé(e) que je perds 
                  mon droit de rétractation dès le début de l'exécution du contrat. »
                </p>
              </div>
              <p className="text-slate-700 leading-relaxed mb-3">
                En cochant cette case, le consommateur consent expressément à ce que l'accès au Service soit immédiat et renonce à son 
                droit de rétractation une fois le Service pleinement exécuté.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">9.3 Modalités d'exercice</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Si le droit de rétractation est applicable (absence de consentement exprès ou service non encore exécuté), le consommateur 
                peut l'exercer en adressant une déclaration dénuée d'ambiguïté à l'adresse email : 
                <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700"> contact@tresopilot.fr</a>.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">9.4 Remboursements</h3>
              <p className="text-slate-700 leading-relaxed">
                En cas de rétractation valable, le remboursement est effectué dans un délai de 14 jours suivant la notification, 
                selon les mêmes moyens de paiement que ceux utilisés pour la transaction initiale, sauf accord contraire. 
                Le remboursement est effectué sans frais supplémentaires.
              </p>
            </section>

            {/* 10. DURÉE — RÉSILIATION — SUPPRESSION DE COMPTE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Durée — Résiliation — Suppression de compte</h2>
              
              <h3 className="font-bold text-slate-800 mb-2 mt-4">10.1 Durée des abonnements</h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Les abonnements Plus et Pro sont souscrits pour une durée d'un mois (formule mensuelle) ou d'un an (formule annuelle), 
                renouvelables par tacite reconduction.</li>
                <li>Le Pack 60 jours prend fin automatiquement 60 jours après l'activation.</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">10.2 Résiliation par l'Utilisateur</h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>L'Utilisateur peut résilier son abonnement à tout moment via le bouton <strong>« Gérer mon abonnement »</strong> 
                disponible dans son Espace Client, qui le redirige vers le portail client Stripe.</li>
                <li>La résiliation prend effet à la fin de la période de facturation en cours. Aucun remboursement prorata n'est 
                effectué pour la période déjà payée, sauf disposition légale contraire.</li>
                <li>L'accès au Service reste actif jusqu'à la date d'expiration de la période payée.</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">10.3 Suppression de compte</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'Utilisateur peut demander la suppression de son compte en contactant le support à l'adresse 
                <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700"> contact@tresopilot.fr</a>.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Les données personnelles sont supprimées ou anonymisées conformément à la politique de confidentialité, 
                sauf obligations légales de conservation (notamment 5 ans pour les données de facturation).
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">10.4 Résiliation par l'éditeur</h3>
              <p className="text-slate-700 leading-relaxed">
                L'éditeur se réserve le droit de suspendre ou résilier un compte en cas de violation des CGU, impayé persistant, 
                ou comportement abusif, après mise en demeure restée sans effet pendant 15 jours.
              </p>
            </section>

            {/* 11. OBLIGATIONS DE L'UTILISATEUR */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Obligations de l'Utilisateur — Usages interdits</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'Utilisateur s'engage à :
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
                <li>Utiliser le Service de manière conforme aux lois et règlements en vigueur.</li>
                <li>Ne pas utiliser le Service à des fins illicites, frauduleuses ou portant atteinte aux droits de tiers.</li>
                <li>Ne pas tenter d'accéder de manière non autorisée aux systèmes ou réseaux connectés au Service.</li>
                <li>Ne pas procéder à du scraping, de l'ingénierie inverse, ou toute tentative d'extraction massive de données.</li>
                <li>Ne pas partager son compte avec des tiers.</li>
                <li>Ne pas utiliser de bots ou d'automatisations abusives.</li>
                <li>Ne pas télécharger de contenu illicite ou contrevenant aux droits de propriété intellectuelle.</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                L'Utilisateur reste responsable de toutes les informations et données qu'il saisit dans l'Application.
              </p>
            </section>

            {/* 12. DONNÉES SAISIES / EXACTITUDE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Données saisies / Exactitude — Limites des estimations</h2>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-red-900 font-semibold mb-2">⚠️ Responsabilité de l'Utilisateur</p>
                <p className="text-red-800 leading-relaxed">
                  L'Utilisateur est seul responsable de l'exactitude des informations qu'il saisit (chiffre d'affaires, charges, 
                  taux, options fiscales, dates). Les calculs et estimations fournis par TrésoPilot dépendent entièrement de ces données.
                </p>
              </div>

              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Nature indicative des calculs :</strong> Les montants de cotisations sociales, d'impôts, de TVA, ainsi que 
                les dates d'échéances sont fournis à titre indicatif et ne constituent pas une garantie. Ils reposent sur les paramètres 
                choisis par l'Utilisateur et sur des règles générales susceptibles d'évoluer.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Échéances et rappels :</strong> Les rappels d'échéances URSSAF, TVA, CFE et autres sont des aides mémoire. 
                L'Utilisateur doit impérativement vérifier les dates et montants exacts sur les portails officiels (urssaf.fr, impots.gouv.fr).
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Absence de garantie de conformité fiscale :</strong> TrésoPilot ne garantit pas que l'utilisation du Service 
                permettra à l'Utilisateur d'éviter pénalités, majorations ou redressements. L'Utilisateur demeure responsable de ses 
                déclarations et paiements.
              </p>
              <p className="text-slate-700 leading-relaxed">
                <strong>Recommandation :</strong> En cas de doute, consulter un expert-comptable, un conseiller fiscal, ou les organismes officiels.
              </p>
            </section>

            {/* 13. DISPONIBILITÉ — MAINTENANCE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Disponibilité — Maintenance</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'éditeur s'efforce de maintenir le Service accessible 24h/24 et 7j/7. Toutefois, le Service est fourni sur la base 
                d'une obligation de moyens.
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Des interruptions temporaires peuvent survenir pour maintenance programmée ou urgente. L'éditeur s'efforce 
                d'informer les Utilisateurs à l'avance lorsque cela est possible.</li>
                <li>L'éditeur ne saurait être tenu responsable des indisponibilités dues à des causes indépendantes de sa volonté 
                (pannes réseau, actes de malveillance, force majeure).</li>
                <li>Aucune indemnisation ne sera due en cas d'interruption du Service, sauf faute grave ou lourde de l'éditeur.</li>
              </ul>
            </section>

            {/* 14. PROPRIÉTÉ INTELLECTUELLE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Propriété intellectuelle</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'Application, le Site, leur structure, leur design, leur marque « TrésoPilot », les logos, les interfaces, 
                les algorithmes, les textes, les graphiques et tout autre contenu sont la propriété exclusive de l'éditeur ou font 
                l'objet de licences d'utilisation.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'Utilisateur bénéficie d'un droit d'utilisation personnel, non exclusif, non cessible et non transférable du Service, 
                limité à un usage conforme aux présentes CGU.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Toute reproduction, représentation, modification, publication, adaptation de tout ou partie du Service sans autorisation 
                écrite préalable de l'éditeur est strictement interdite et constitue une contrefaçon.
              </p>
              <p className="text-slate-700 leading-relaxed mt-3">
                <strong>Données de l'Utilisateur :</strong> L'Utilisateur conserve l'intégralité des droits sur les données qu'il 
                saisit dans l'Application. L'éditeur ne revendique aucun droit de propriété sur ces données.
              </p>
            </section>

            {/* 15. DONNÉES PERSONNELLES — COOKIES */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Données personnelles — Cookies</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'éditeur collecte et traite des données à caractère personnel dans le cadre de l'utilisation du Service. 
                Ces traitements sont décrits en détail dans la <strong><a href="/confidentialite" className="text-emerald-600 hover:text-emerald-700">Politique de confidentialité</a></strong>.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Synthèse</h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li><strong>Finalités :</strong> Gestion du compte, exécution du contrat, facturation, envoi de rappels 
                (si consentement), amélioration du Service, support client.</li>
                <li><strong>Base légale :</strong> Exécution du contrat, intérêt légitime, consentement (newsletters, cookies non essentiels).</li>
                <li><strong>Conservation :</strong> Durées proportionnées aux finalités (voir Politique de confidentialité), 
                minimum 5 ans pour les données de facturation conformément aux obligations légales.</li>
                <li><strong>Droits :</strong> Droit d'accès, rectification, effacement, limitation, portabilité, opposition. 
                Contactez-nous à : <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a></li>
                <li><strong>Réclamation :</strong> En cas de litige, l'Utilisateur peut saisir la CNIL (www.cnil.fr).</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Cookies et traceurs</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Le Site et l'Application peuvent utiliser des cookies et autres traceurs pour :
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
                <li>Assurer le bon fonctionnement du Service (cookies strictement nécessaires).</li>
                <li>Analyser l'audience et améliorer l'expérience utilisateur (cookies analytiques, si activés).</li>
                <li>Personnaliser le contenu (si applicable).</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                Pour plus d'informations et gérer vos préférences, consultez notre <strong><a href="/cookies" className="text-emerald-600 hover:text-emerald-700">Politique Cookies</a></strong>.
              </p>
            </section>

            {/* 16. RESPONSABILITÉ — LIMITATION */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Responsabilité — Limitation</h2>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                <p className="text-amber-900 font-semibold mb-2">⚠️ Limitation importante</p>
                <p className="text-amber-800 leading-relaxed">
                  TrésoPilot ne fournit ni conseil fiscal, ni conseil juridique, ni service d'expertise-comptable. 
                  Aucune garantie n'est donnée quant à la conformité fiscale ou sociale des actions de l'Utilisateur.
                </p>
              </div>

              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Exclusion des dommages indirects :</strong> Dans les limites autorisées par la loi, l'éditeur ne saurait être 
                tenu responsable des dommages indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le Service 
                (perte de données, perte de chiffre d'affaires, perte de clients, sanctions fiscales, etc.).
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Plafonnement de responsabilité :</strong> La responsabilité globale de l'éditeur, tous dommages confondus, 
                est limitée au montant total des sommes effectivement payées par l'Utilisateur au cours des 12 mois 
                précédant le fait générateur.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Force majeure :</strong> L'éditeur ne sera pas tenu pour responsable de tout retard ou inexécution de ses 
                obligations résultant d'un cas de force majeure au sens de la jurisprudence française.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Ces limitations ne s'appliquent pas en cas de faute lourde ou dolosive de l'éditeur, ni en cas de dommages corporels.
              </p>
            </section>

            {/* 17. SUPPORT */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">17. Support</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Un service de support est disponible pour répondre aux questions techniques et fonctionnelles :
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
                <li><strong>Email :</strong> <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a></li>
                <li><strong>WhatsApp Business :</strong> +33 7 56 90 15 80</li>
                <li><strong>Délai de réponse :</strong> Sous 48h ouvrées pour les abonnés Plus/Pro, 
                sous 5 jours ouvrés pour les utilisateurs gratuits</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                Les Utilisateurs abonnés bénéficient d'un support prioritaire. En cas d'abus ou de demandes répétées non fondées, 
                l'éditeur se réserve le droit de limiter l'accès au support.
              </p>
            </section>

            {/* 18. MODIFICATION DES CGU */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">18. Modification des CGU</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                L'éditeur se réserve le droit de modifier les présentes CGU à tout moment. Les modifications sont portées à la 
                connaissance des Utilisateurs par email et/ou notification in-app, avec un délai de préavis de 30 jours.
              </p>
              <p className="text-slate-700 leading-relaxed">
                La poursuite de l'utilisation du Service après l'entrée en vigueur des nouvelles CGU vaut acceptation de celles-ci. 
                Si l'Utilisateur n'accepte pas les modifications, il peut résilier son abonnement conformément à l'article 10.
              </p>
            </section>

            {/* 19. RÈGLEMENT DES LITIGES — MÉDIATION */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">19. Règlement des litiges — Médiation de la consommation</h2>
              
              <h3 className="font-bold text-slate-800 mb-2 mt-4">19.1 Médiation pour les consommateurs</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Conformément aux articles L. 611-1 et suivants du Code de la consommation, les consommateurs ont accès à un dispositif 
                de médiation gratuit en cas de litige non résolu avec l'éditeur.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Procédure préalable :</strong> Avant de saisir le médiateur, le consommateur doit avoir tenté de résoudre 
                le litige directement auprès du service client de TrésoPilot par écrit 
                (<a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a>).
              </p>
              
              <div className="bg-slate-50 rounded-xl p-6 space-y-2 mb-4">
                <p><strong>Médiateur de la consommation :</strong></p>
                <p className="text-slate-700">
                  En cas de litige non résolu, vous pouvez saisir gratuitement un médiateur de la consommation. 
                  Les coordonnées du médiateur compétent seront communiquées sur demande à 
                  <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700"> contact@tresopilot.fr</a>.
                </p>
              </div>

              <p className="text-slate-700 leading-relaxed mb-3">
                La saisine du médiateur ne peut intervenir qu'après une réclamation écrite préalable restée infructueuse.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Plateforme européenne de règlement en ligne des litiges :</strong> Les consommateurs résidant dans l'Union européenne 
                peuvent également soumettre une réclamation via la plateforme ODR : 
                <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline">https://ec.europa.eu/consumers/odr/</a>.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">19.2 Professionnels</h3>
              <p className="text-slate-700 leading-relaxed">
                Pour les litiges entre professionnels, tentative de résolution amiable préalable recommandée. À défaut, compétence 
                des juridictions compétentes (cf. article 20).
              </p>
            </section>

            {/* 20. DROIT APPLICABLE — JURIDICTION */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">20. Droit applicable — Juridiction compétente</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Les présentes CGU sont régies par le <strong>droit français</strong>.
              </p>
              
              <h3 className="font-bold text-slate-800 mb-2 mt-4">Consommateurs</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                En application de l'article R. 631-3 du Code de la consommation, tout litige relatif aux présentes CGU qui n'aurait pu 
                être résolu à l'amiable ou par médiation peut être porté devant les juridictions dont dépend le domicile du consommateur.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Professionnels</h3>
              <p className="text-slate-700 leading-relaxed">
                Pour les litiges entre professionnels, compétence exclusive est attribuée aux tribunaux compétents du ressort de 
                Paris, même en cas de pluralité de défendeurs ou d'appel en garantie.
              </p>
            </section>

            {/* CONTACT */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Contact</h2>
              <div className="bg-emerald-50 rounded-xl p-6 space-y-3">
                <p className="text-slate-700">
                  Pour toute question relative aux présentes CGU, à l'utilisation du Service ou à votre compte :
                </p>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">
                      contact@tresopilot.fr
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">WhatsApp Business</p>
                    <a href="tel:+33756901580" className="text-emerald-600 hover:text-emerald-700">
                      +33 7 56 90 15 80
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <div className="border-t border-slate-200 pt-6 mt-8 text-center text-slate-500 text-sm space-y-2">
              <p><Tagline /></p>
              <p>© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
              <p className="mt-2">
                Document légal — Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}